import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { CTASection } from "@/components/CTASection";
import { IMG, INDUSTRIES } from "@/data/content";
import { CAPABILITY_AREAS, CAPABILITIES_INTRO } from "@/data/site";
import { SEO } from "@/data/seo";

export default function Capabilities() {
  useSeo({ ...SEO["/capabilities"], image: IMG.aiAbstract, path: "/capabilities" });

  return (
    <>
      <PageHero
        eyebrow="Capabilities"
        titleLines={["Where PMN", "leads."]}
        subtitle={CAPABILITIES_INTRO.body}
        image={IMG.aiAbstract}
      />

      {/* Seven areas of practice */}
      <section className="bg-paper py-24 lg:py-32" data-testid="capability-areas">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Areas of practice</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">
              Seven capabilities, one accountable firm.
            </h2>
          </Reveal>
          <ol className="mt-16 divide-y divide-charcoal/10 border-y border-charcoal/10">
            {CAPABILITY_AREAS.map((c, i) => (
              <Reveal as="li" key={c.id} id={c.id} delay={Math.min(i, 4) * 0.05} className="grid gap-4 py-9 scroll-mt-28 lg:grid-cols-12 lg:gap-10 lg:py-11" data-testid={`capability-${c.id}`}>
                <span className="font-serif text-2xl text-teal lg:col-span-1" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="text-2xl font-normal tracking-tightish text-charcoal lg:col-span-4">{c.title}</h3>
                <p className="text-base leading-[1.8] text-charcoal/70 lg:col-span-7">{c.body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-charcoal py-24 text-paper lg:py-32" data-testid="capability-industries">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow dark>Where this work happens</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish sm:text-4xl lg:text-5xl">
              Regulated and growth-stage environments.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-paper/15 bg-paper/15 md:grid-cols-2">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.id} delay={i * 0.06} className="bg-charcoal">
                <Link to={`/case-studies#${ind.id}`} className="group block h-full p-8 lg:p-10" data-testid={`industry-tile-${ind.id}`}>
                  <h3 className="flex items-start justify-between gap-4 font-serif text-2xl tracking-tightish text-paper">
                    {ind.name}
                    <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-teal-light transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} aria-hidden="true" />
                  </h3>
                  <p className="mt-4 text-sm leading-[1.8] text-paper/60">{ind.note}</p>
                  <span className="mt-6 inline-block text-xs uppercase tracking-mega text-teal-light">Related experience</span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Put it to work"
        title="Which of these does your initiative need first?"
        body="Describe the program and we'll tell you where PMN would start — an assessment, embedded leadership, or a PMN-led team."
        image={IMG.boardroomWarm}
      />
    </>
  );
}
